"use client";

import { FC, FormEvent, ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { catchError } from "@erp/shared-libs/utils";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Separator } from "../ui/separator";

interface DeleteConfirmDialogProps {
  /** 對話框標題 */
  title: string;
  /** 說明文字，可傳 ReactNode 以強調刪除目標名稱 */
  description: ReactNode;
  /** 需輸入一致才可刪除的確認文字（通常為刪除目標名稱） */
  confirmText: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 確認按鈕文字，預設為「刪除」 */
  confirmButtonText?: string;
  /** 確認刪除；未 throw 才會關閉對話框 */
  onConfirm: () => void | Promise<void>;
}

/**
 * 高危險刪除確認對話框
 * 需輸入與 confirmText 完全一致的文字才可按下刪除，防止誤刪
 */
export const DeleteConfirmDialog: FC<DeleteConfirmDialogProps> = ({
  open,
  onOpenChange,
  ...props
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideClose className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DeleteConfirmDialogContent onOpenChange={onOpenChange} {...props} />
      </DialogContent>
    </Dialog>
  );
};

const DeleteConfirmDialogContent: FC<Omit<DeleteConfirmDialogProps, "open">> = ({
  title,
  description,
  confirmText,
  confirmButtonText,
  onOpenChange,
  onConfirm,
}) => {
  // ==================================================
  // Hooks
  // ==================================================
  const { t } = useTranslation(["common"]);

  /**
   * 使用者輸入的確認文字
   */
  const [inputValue, setInputValue] = useState("");

  /**
   * 是否正在執行刪除
   */
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ==================================================
  // Variables
  // ==================================================
  /**
   * 輸入文字是否與確認文字完全一致
   */
  const isMatch = inputValue.trim() === confirmText.trim();

  // ==================================================
  // Functions
  // ==================================================
  /**
   * 複製確認文字到剪貼簿
   */
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(confirmText);
    toast.success("已複製內容到剪貼簿", { duration: 500 });
  }, [confirmText]);

  /**
   * 送出刪除
   * dialog 掛在 portal 內，需 stopPropagation 防止 submit 冒泡誤觸外層表單；
   * onConfirm 未 throw 才關閉對話框
   */
  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      event.stopPropagation();

      setIsSubmitting(true);
      const [error] = await catchError(async () => onConfirm());
      setIsSubmitting(false);

      if (!error) {
        onOpenChange(false);
      }
    },
    [onConfirm, onOpenChange]
  );

  // ==================================================
  // Render
  // ==================================================
  return (
    <form onSubmit={handleSubmit} className="flex flex-col">
      <div className="flex flex-col pt-4">
        <DialogHeader className="px-4 text-left">
          <DialogTitle asChild>
            <div className="text-lg font-bold">{title}</div>
          </DialogTitle>
          <DialogDescription className="mb-4"></DialogDescription>
        </DialogHeader>
        <Separator />
        <div className="bg-background flex flex-col gap-3 px-4 py-6 text-sm">
          <div>{description}</div>
          {/* 輸入 {confirmText} 以確認 */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span>{t("common:dialog.delete_confirm.input_prefix")}</span>
            <Button
              type="button"
              title="複製"
              variant="secondary"
              size="xs"
              className="gap-1.5 rounded-md px-2 font-mono font-bold"
              onClick={handleCopy}
            >
              {confirmText}
              <Copy className="size-3!" />
            </Button>
            <span>{t("common:dialog.delete_confirm.input_suffix")}</span>
          </div>
          <Input
            type="text"
            autoComplete="off"
            placeholder={confirmText}
            value={inputValue}
            disabled={isSubmitting}
            onChange={(e) => setInputValue(e.target.value)}
          />
        </div>
      </div>
      <Separator />
      <div className="flex items-end justify-end px-4 py-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="px-8"
          disabled={isSubmitting}
          onClick={() => onOpenChange(false)}
        >
          {t("common:button.cancel")}
        </Button>
        <Button
          type="submit"
          variant="destructive"
          size="sm"
          className="ml-2 px-8"
          disabled={!isMatch || isSubmitting}
          isLoading={isSubmitting}
        >
          {confirmButtonText ?? t("common:button.delete")}
        </Button>
      </div>
    </form>
  );
};
